import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle, FileCheck, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/content";
import { ACCEPTANCE_TEXT } from "../data/terms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgreeTerms() {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [state, setState] = useState("loading");
  const [agreeing, setAgreeing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/leads/${leadId}/public`)
      .then((res) => {
        setLead(res.data);
        setState(res.data.terms_accepted ? "done" : "ready");
      })
      .catch(() => setState("notfound"));
  }, [leadId]);

  const agree = async () => {
    setAgreeing(true);
    try {
      await axios.post(`${API}/leads/${leadId}/accept-terms`);
      setState("done");
      toast.success("Thank you! Your appointment is confirmed.");
    } catch {
      toast.error("Something went wrong. Please call us at " + PHONE_DISPLAY);
      setAgreeing(false);
    }
  };

  return (
    <main data-testid="agree-terms-page" className="pt-20 md:pt-24 min-h-[80vh] flex items-center justify-center px-6">
      <div className="max-w-lg w-full bg-[#121212] border border-zinc-800 rounded-xl p-8 sm:p-10 my-16">
        {state === "loading" && (
          <div className="text-center py-10"><Loader2 className="h-10 w-10 animate-spin text-blue-500 mx-auto" /></div>
        )}

        {state === "notfound" && (
          <div className="text-center" data-testid="agree-notfound">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-white mb-3">Link not found</h1>
            <p className="text-zinc-400 mb-6">This agreement link isn't valid. Give us a call and we'll sort it out.</p>
            <a href={PHONE_TEL} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-3 font-semibold transition-colors">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </div>
        )}

        {state === "ready" && (
          <div data-testid="agree-ready">
            <div className="bg-blue-600/10 border border-blue-500/20 rounded-sm w-12 h-12 flex items-center justify-center mb-5">
              <FileCheck className="h-6 w-6 text-blue-500" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
              Hi {lead?.name?.split(" ")[0]}, one last step
            </h1>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Before we confirm your appointment{lead?.service_needed ? ` for ${lead.service_needed}` : ""},
              please review and accept our{" "}
              <Link to="/terms" target="_blank" className="text-blue-400 underline underline-offset-2" data-testid="agree-terms-link">
                Terms of Service and Repair Authorization
              </Link>.
            </p>
            <p className="text-zinc-500 text-sm leading-relaxed mb-8 bg-[#0a0a0a] border border-zinc-800 rounded-sm p-4">
              {ACCEPTANCE_TEXT}
            </p>
            <button
              data-testid="agree-button"
              onClick={agree}
              disabled={agreeing}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-8 py-4 font-semibold transition-colors"
            >
              {agreeing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
              ✓ I Agree
            </button>
          </div>
        )}

        {state === "done" && (
          <div className="text-center" data-testid="agree-done">
            <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-5" />
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">You're all set!</h1>
            <p className="text-zinc-400 leading-relaxed mb-8">
              Your acceptance has been recorded and your appointment is confirmed.
              By scheduling this appointment, you confirmed your acceptance of our{" "}
              <Link to="/terms" target="_blank" className="text-blue-400 underline underline-offset-2">
                Terms of Service and Repair Authorization
              </Link>.
            </p>
            <a href={PHONE_TEL} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-3 font-semibold transition-colors">
              <Phone className="h-4 w-4" /> Questions? {PHONE_DISPLAY}
            </a>
          </div>
        )}
      </div>
    </main>
  );
}
